import { Component, OnInit, Input, ChangeDetectorRef, signal, ViewChild } from "@angular/core";
import { Subject } from "rxjs";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { BudgetService } from "./budget.service";
import { S25Util } from "../../../util/s25-util";
import { Budget } from "./Budget";
import { OrganizationService, WSOrganization } from "../../../services/organization.service";
import { Bind } from "../../../decorators/bind.decorator";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { Table } from "../../s25-table/Table";
import { BudgetCreateModalComponent } from "./admin/budget.create.modal.component";
import { S25PricingUtil } from "../../s25-pricing/s25.pricing.util";

@TypeManagerDecorator("s25-ng-org-budget")
@Component({
    selector: "s25-ng-org-budget",
    template: `
        @if (init) {
            <div class="budget-dashboard">
                <!-- Header Section -->
                <div class="budget-header">
                    <h2 class="budget-title">Budget Dashboard</h2>
                    <div class="fiscal-year">Fiscal Year: {{ budgetSummary?.fiscalYear || "N/A" }}</div>
                </div>

                <!-- Loading State -->
                @if (isLoading()) {
                    <s25-ng-loading-inline-static [text]="'Loading budget data...'"></s25-ng-loading-inline-static>
                }

                <!-- Error State -->
                @if (error) {
                    <div class="error-message">
                        <p>{{ error }}</p>
                        <s25-ng-button
                            [type]="'outline'"
                            [buttonText]="'Retry'"
                            [onClick]="loadBudgetData"
                        ></s25-ng-button>
                    </div>
                }

                <!-- Budget Summary -->
                @if (!isLoading() && !error) {
                    <!-- Budget Table -->
                    <div class="budgets-table-section">
                        <div class="section-header">
                            <h3>Organization Budgets</h3>
                        </div>

                        <s25-ng-table
                            [dataSource]="budgetTableData"
                            [hasRefresh]="true"
                            [hasFilter]="true"
                            [columnSortable]="true"
                            [hasTotalRowCount]="true"
                            [caption]="'Organization Budgets'"
                            [noResultsMessage]="'No budgets found for this organization'"
                        ></s25-ng-table>
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .budget-dashboard {
            padding: 1.5rem;
            max-width: 1200px;
            margin: 0 auto;
        }

        .budget-header {
            text-align: center;
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid #e5e5e5;
        }

        .budget-title {
            font-size: 1.5rem;
            color: #666;
            margin: 0 0 0.5rem 0;
        }

        .fiscal-year {
            font-size: 1rem;
            color: #888;
            font-weight: 500;
        }

        .error-message {
            text-align: center;
            padding: 2rem;
            background: #fff3f3;
            border: 1px solid #ffcdd2;
            border-radius: 8px;
            margin: 1rem 0;
        }

        .error-message p {
            color: #d32f2f;
            margin-bottom: 1rem;
        }

        .budgets-table-section {
            margin-bottom: 2rem;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            overflow: hidden;
        }

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.5rem;
            background: #f8f9fa;
            border-bottom: 1px solid #e5e5e5;
        }

        .section-header h3 {
            margin: 0;
            font-size: 1.2rem;
            color: #333;
            font-weight: 600;
        }

        /* Budget Table Styles */
        ::ng-deep .budgets-table-section .s25ngTable {
            border: none;
            box-shadow: none;
        }

        ::ng-deep .budgets-table-section .rose-object-table-header {
            margin: 0;
            padding: 1rem 1.5rem;
            background: transparent;
        }

        @media (max-width: 768px) {
            .budget-dashboard {
                padding: 1rem;
            }

            .budget-actions s25-ng-button {
                width: 100%;
            }

            .section-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }

            .section-header s25-ng-button {
                width: 100%;
            }
        }
    `,
    standalone: true,
    imports: [S25LoadingInlineModule, S25ButtonComponent, S25PipesModule, S25TableModule],
})
export class OrgBudgetComponent implements OnInit {
    @Input() itemId: number;
    @ViewChild("budgetCreateModal") budgetCreateModal!: BudgetCreateModalComponent;

    init = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    isCreatingPlan = false;
    error: string | null = null;

    orgDetails: any;
    budgetSummary: Budget.BudgetSummary | null = null;
    budgetTableData: Table.DataSource;
    budgets: Budget.OrgBudget[] = [];

    private destroy$ = new Subject<void>();

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.initializeComponent();
    }

    @Bind
    private async initializeComponent(): Promise<void> {
        // Cancel any ongoing requests
        this.destroy$.next();

        // Reset component state
        this.init.set(false);
        this.isLoading.set(true);
        this.isCreatingPlan = false;
        this.error = null;
        this.orgDetails = {};
        this.budgetSummary = null;
        this.budgets = [];

        this.cd.detectChanges();

        if (this.itemId) {
            const [resp, error] = await S25Util.Maybe(this.loadBudgetData());
            this.init.set(true);
            if (error) {
                this.loadingDone("Failed to load budget data. Please try again.");
            } else {
                this.loadingDone();
            }
        } else {
            this.loadingDone("No organization ID provided.");
        }
    }

    @Bind
    async loadBudgetData(): Promise<void> {
        this.isLoading.set(true);
        this.error = null;
        this.cd.detectChanges();

        try {
            // Load organization details
            const [orgData, orgError] = await S25Util.Maybe(OrganizationService.getOrganizationById(this.itemId));
            if (orgError) {
                throw new Error(`Failed to load organization: ${S25Util.errorText(orgError)}`);
            }
            this.orgDetails = orgData;

            // Load budget summary
            const [budgetData, budgetError] = await S25Util.Maybe(BudgetService.getBudgetSummary(this.itemId));
            if (budgetError) {
                throw new Error(`Failed to load budget summary: ${S25Util.errorText(budgetError)}`);
            }
            this.budgetSummary = budgetData;

            // Load all budgets for the table
            const [budgetsData, budgetsError] = await S25Util.Maybe(BudgetService.getAllOrgBudgets(this.itemId));
            if (budgetsError) {
                this.budgets = [];
            } else {
                this.budgets = budgetsData;
                this.budgetTableData = this.createBudgetTableDataSource();
            }
        } catch (error) {
            this.error = error instanceof Error ? error.message : "An unexpected error occurred";
            throw error;
        } finally {
            this.isLoading.set(false);
            this.cd.detectChanges();
        }
    }

    createBudgetTableDataSource(): Table.DataSource {
        return {
            type: "unpaginated",
            dataSource: this.getBudgetTableRows,
            columns: [
                {
                    id: "fiscalYear",
                    header: "Fiscal Year",
                    minWidth: 120,
                    cells: {
                        text: "",
                        className: "font-weight-medium",
                    },
                },
                {
                    id: "name",
                    header: "Budget Name",
                    minWidth: 200,
                    cells: {
                        text: "",
                        className: "font-weight-medium",
                    },
                },
                {
                    id: "status",
                    header: "Status",
                    minWidth: 100,
                    align: "center",
                    cells: {
                        text: "",
                        className: "budget-status",
                    },
                },
                {
                    id: "totalAllocated",
                    header: "Total Allocated",
                    minWidth: 150,
                    align: "right",
                    cells: {
                        text: "",
                        className: "currency-amount",
                    },
                },
                {
                    id: "utilizationPercentage",
                    header: "Utilization",
                    minWidth: 100,
                    align: "center",
                    cells: {
                        text: "",
                        className: "utilization-percentage",
                    },
                },
                {
                    id: "budgetPlansCount",
                    header: "Budget Plans",
                    minWidth: 120,
                    align: "center",
                    cells: {
                        text: "",
                        className: "plans-count",
                    },
                },
            ],
        };
    }

    @Bind
    async getBudgetTableRows(): Promise<Table.DataSourceResponse> {
        const rows: Table.Row[] = this.budgets.map((budget) => ({
            id: budget.budgetId,
            name: budget.name,
            data: budget,
            cells: {
                fiscalYear: { text: budget.fiscalYear.name },
                name: { text: budget.name },
                status: {
                    text: this.getBudgetStatus(budget),
                    className: `budget-status ${this.getBudgetStatusClass(budget)}`,
                },
                totalAllocated: {
                    text: S25PricingUtil.formatCurrency(budget.totalAllocated, budget.currency || "USD"),
                },
                utilizationPercentage: {
                    text: `${Budget.getUtilization(budget).toFixed(1)}%`,
                },
                budgetPlansCount: {
                    text: budget.budgetPlans?.length?.toString() || "0",
                },
            },
        }));

        return {
            rows,
            totalRows: rows.length,
        };
    }

    getBudgetStatus(budget: Budget.OrgBudget): string {
        const now = new Date();
        const startDate = new Date(budget.startDate);
        const endDate = new Date(budget.endDate);

        if (now < startDate) {
            return "Upcoming";
        } else if (now > endDate) {
            return "Expired";
        } else {
            const utilization = Budget.getUtilization(budget);
            if (utilization >= 0.9) {
                return "Critical";
            } else if (utilization >= 0.75) {
                return "Warning";
            } else {
                return "Active";
            }
        }
    }

    getBudgetStatusClass(budget: Budget.OrgBudget): string {
        const status = this.getBudgetStatus(budget);
        switch (status) {
            case "Active":
                return "status-active";
            case "Warning":
                return "status-warning";
            case "Critical":
                return "status-critical";
            case "Expired":
                return "status-expired";
            case "Upcoming":
                return "status-upcoming";
            default:
                return "status-unknown";
        }
    }

    loadingDone(errorMsg?: string): void {
        this.isLoading.set(false);
        this.error = errorMsg || "";
        this.cd.detectChanges();
    }
}
